import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, ChevronRight, Scale, BookOpen, AlertCircle, CheckCircle, ArrowLeft, Lock, FileText, Info } from 'lucide-react';
import '../styles/App.css';

import BrandLogo from '../components/common/BrandLogo';

function Terms() {
  const location = useLocation();
  const lastRevised = "12 May, 2026";
  
  // Tab handling: terms, privacy, risk
  const [activeTab, setActiveTab] = useState('terms');

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (['terms', 'privacy', 'risk'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location]);

  const tabs = [
    { id: 'terms', title: 'Terms of Use', icon: <Scale size={18} /> },
    { id: 'privacy', title: 'Privacy Policy', icon: <Lock size={18} /> },
    { id: 'risk', title: 'Risk Disclosure', icon: <AlertCircle size={18} /> }
  ];

  return (
    <div className="terms-page" style={{ 
      minHeight: '100vh', 
      background: 'var(--bg-primary)', 
      color: 'var(--text-primary)',
      fontFamily: "'Outfit', sans-serif"
    }}>
      {/* Premium Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
        padding: '6rem 2rem 4rem',
        textAlign: 'center',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-20%', left: '-10%', width: '600px', height: '600px',
          background: 'radial-gradient(circle, rgba(252, 213, 53, 0.1) 0%, transparent 70%)', zIndex: 0, filter: 'blur(80px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
          <Link to="/" style={{ 
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem', 
            color: 'var(--accent-primary)', textDecoration: 'none', fontWeight: '700',
            marginBottom: '2rem', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.1em'
          }}>
            <ArrowLeft size={16} /> Return to Platform
          </Link>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: '900', marginBottom: '1.5rem', letterSpacing: '-0.03em' }}>
            Legal <span className="gradient-text">Documentation</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.15rem', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6' }}>
            Transparency and regulatory integrity are the cornerstones of Quantis Capital. Our legal framework ensures the highest level of institutional governance.
          </p>
        </div>

        {/* Tab Switcher */}
        <div style={{ 
          maxWidth: '600px', margin: '3rem auto 0', display: 'flex', gap: '0.5rem', 
          background: 'var(--bg-secondary)', padding: '0.5rem', borderRadius: '16px',
          border: '1px solid var(--border-color)', position: 'relative', zIndex: 2
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                padding: '0.8rem', borderRadius: '12px', border: 'none', cursor: 'pointer',
                fontWeight: '700', fontSize: '0.95rem', transition: 'all 0.3s',
                background: activeTab === tab.id ? 'var(--accent-primary)' : 'transparent',
                color: activeTab === tab.id ? '#000' : 'var(--text-secondary)',
                boxShadow: activeTab === tab.id ? '0 8px 20px rgba(252, 213, 53, 0.2)' : 'none'
              }}
            >
              {tab.icon} {tab.title}
            </button>
          ))}
        </div>
      </div>

      <div className="terms-container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '4rem 2rem' }}>
        <article style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--text-primary)' }}>
          
          <div style={{ marginBottom: '3rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '2rem' }}>
             <p style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Last revised: {lastRevised}</p>
          </div>

          {activeTab === 'terms' && (
            <div className="fade-in">
              <h2 style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <Scale size={32} color="var(--accent-primary)" /> Terms of Use
              </h2>
              <div className="legal-content">
                <p>By accessing the Quantis Capital platform, you acknowledge that you have read, understood, and agreed to be bound by these Terms of Use. These terms govern your use of our algorithmic trading, wealth management, and custodial services.</p>
                
                <h3 style={sectionHeader}>1. Eligibility & Registration</h3>
                <p>Quantis Capital services are available exclusively to individuals who are at least 18 years of age. Institutional clients must provide valid corporate documentation. We reserve the right to refuse service to any jurisdiction where our operations may be restricted by local law.</p>
                
                <h3 style={sectionHeader}>2. Algorithmic Execution</h3>
                <p>Our platform utilizes proprietary quantitative models to execute trades across Forex, Digital Assets, and Commodities. While our models are engineered for institutional-grade performance, market outcomes are subject to volatility. Quantis Capital does not guarantee specific daily returns, and historical performance is not indicative of future results.</p>
                
                <h3 style={sectionHeader}>3. Account Funding & Withdrawals</h3>
                <p>Accounts must be funded using approved digital assets or wire transfers. Quantis Capital reserves the right to deactivate any account that remains unfunded for more than 48 hours. Withdrawals are processed according to the liquidity terms of your selected investment vehicle.</p>
                
                <h3 style={sectionHeader}>4. Fees & Commissions</h3>
                <p>Our transparent fee structure includes a management fee and a performance-based incentive (Hurdle Rate). Detailed fee schedules are provided within each investment prospectus. No hidden costs or soft-dollar arrangements are permitted.</p>
                
                <div style={alertBox}>
                  <strong>Binding Arbitration:</strong> Any disputes arising from these terms shall be resolved through legally binding arbitration in accordance with the rules of the International Chamber of Commerce.
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="fade-in">
              <h2 style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <Lock size={32} color="var(--accent-primary)" /> Privacy Policy
              </h2>
              <div className="legal-content">
                <p>Quantis Capital is committed to protecting the privacy and confidentiality of our investors' personal and financial data. We adhere to GDPR and SOC 2 Type II data protection standards.</p>
                
                <h3 style={sectionHeader}>1. Information Collection</h3>
                <p>We collect essential information required for KYC (Know Your Customer) and AML (Anti-Money Laundering) compliance, including government-issued identification, proof of address, and source of wealth documentation.</p>
                
                <h3 style={sectionHeader}>2. Data Usage & Processing</h3>
                <p>Your data is used solely to provide platform services, ensure security, and meet regulatory requirements. We do not sell or lease investor data to third-party marketing firms.</p>
                
                <h3 style={sectionHeader}>3. Security Measures</h3>
                <p>All sensitive data is encrypted at rest using AES-256 and in transit using TLS 1.3. Access to investor records is restricted through strict multi-factor authentication and role-based access controls.</p>
                
                <h3 style={sectionHeader}>4. Third-Party Sharing</h3>
                <p>We may share data with regulated custodial partners, banking institutions, and law enforcement agencies only when required for service execution or legal compliance.</p>
              </div>
            </div>
          )}

          {activeTab === 'risk' && (
            <div className="fade-in">
              <h2 style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <AlertCircle size={32} color="var(--accent-primary)" /> Risk Disclosure
              </h2>
              <div className="legal-content">
                <p>Investing in digital assets, forex, and algorithmic strategies involves substantial risk. This disclosure provides a summary of the primary risks associated with the Quantis Capital platform.</p>
                
                <h3 style={sectionHeader}>1. Market Volatility</h3>
                <p>Global markets can fluctuate significantly due to macroeconomic events, geopolitical shifts, and technological changes. Investors may sustain a total loss of their principal capital.</p>
                
                <h3 style={sectionHeader}>2. Algorithmic Risk</h3>
                <p>Systematic trading models are based on historical data and statistical probabilities. Unforeseen market "Black Swan" events can lead to model performance deviations.</p>
                
                <h3 style={sectionHeader}>3. Cybersecurity Risk</h3>
                <p>Despite our institutional-grade security and cold-storage protocols, the digital asset ecosystem is susceptible to technological failures and sophisticated cyber-attacks.</p>
                
                <h3 style={sectionHeader}>4. Regulatory Uncertainty</h3>
                <p>The regulatory landscape for digital assets is evolving. Future legislative changes in your jurisdiction may impact your ability to access or withdraw assets.</p>
                
                <div style={{ ...alertBox, border: '1.5px solid rgba(252, 213, 53, 0.3)', background: 'rgba(252, 213, 53, 0.05)' }}>
                  <p style={{ margin: 0, color: 'var(--text-primary)', fontWeight: '700' }}>
                    Risk Warning: Never invest capital that you cannot afford to lose. Quantis Capital does not provide financial advice; please consult with an independent financial advisor.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div style={{ 
            padding: '4rem 2rem', background: 'var(--bg-secondary)', 
            borderRadius: '32px', textAlign: 'center', marginTop: '6rem',
            border: '1px solid var(--border-color)', position: 'relative'
          }}>
            <BrandLogo />
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '1.5rem' }}>
              &copy; {new Date().getFullYear()} Quantis Capital Global Ecosystem. All rights reserved.<br />
              Quantis Capital is a registered digital asset manager in multiple jurisdictions.
            </p>
          </div>
        </article>
      </div>

      <style>{`
        .legal-content p { margin-bottom: 1.5rem; color: var(--text-secondary); }
        .legal-content strong { color: var(--text-primary); }
        .fade-in { animation: fadeIn 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

const sectionHeader = {
  fontSize: '1.4rem',
  fontWeight: '800',
  marginTop: '2.5rem',
  marginBottom: '1rem',
  color: 'var(--text-primary)'
};

const alertBox = {
  padding: '2rem',
  background: 'rgba(239, 68, 68, 0.05)',
  borderRadius: '24px',
  border: '1.5px dashed rgba(239, 68, 68, 0.3)',
  margin: '3rem 0',
  color: '#ef4444',
  fontWeight: '700',
  fontSize: '0.95rem'
};

export default Terms;
